import { Alert, Chip, Stack, Typography } from "@mui/material";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { type InputProps, answerIds } from "../answers";
import { DateField } from "../DateField";
import { useDocumentTypes } from "../documentTypes";
import { type UploadedFile, useFileActions } from "../fileActions";
import { FilePicker } from "../FilePicker";

export const FileInput: React.FC<InputProps<"file">> = ({ question, answer, onChange, error }) => {
    const { formatMessage, formatList } = useIntl();
    const actions = useFileActions();
    const [mismatched, setMismatched] = useState<string[]>([]);
    const files = answerIds(answer?.value);
    const documentTypes = useDocumentTypes();
    const requested = (question.documentTypeIds ?? []).flatMap((id) => documentTypes.filter((type) => type.id === id));
    const flagged = mismatched.filter((name) => files.includes(name));

    const suggest = (uploaded: UploadedFile[]) => {
        if (!actions || (!question.expiry && requested.length === 0)) return;
        uploaded.forEach(({ fileName, fileId }) =>
            actions
                .suggest(fileId)
                .then(({ expiryDate, matchesRequestedType }) => {
                    if (question.expiry && expiryDate) {
                        onChange((current) => (current?.expiry ? current : { ...current, expiry: expiryDate }));
                    }
                    if (matchesRequestedType === false) setMismatched((current) => [...current, fileName]);
                })
                .catch(() => undefined)
        );
    };

    return (
        <Stack spacing={2}>
            {requested.length > 1 && (
                <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 1 }}>
                    <Typography variant="textSm" color="textTextSecondary">
                        <FormattedMessage defaultMessage="Any one of:" />
                    </Typography>
                    {requested.map((type) => (
                        <Chip key={type.id} size="small" label={type.name} />
                    ))}
                </Stack>
            )}
            <FilePicker
                questionKey={question.id}
                answer={answer}
                multi={question.multi}
                files={files}
                error={error}
                onChange={(update) => onChange((current) => ({ ...current, value: update(answerIds(current?.value)) }))}
                onUploaded={suggest}
            >
                {flagged.length > 0 && requested.length > 0 && (
                    <Alert severity="warning">
                        <FormattedMessage
                            defaultMessage="{files} does not look like {types}. Check that you uploaded the right document."
                            values={{
                                files: formatList(flagged),
                                types: formatList(
                                    requested.map((type) => type.name),
                                    { type: "disjunction" }
                                ),
                            }}
                        />
                    </Alert>
                )}
                {question.expiry && files.length > 0 && (
                    <DateField
                        label={formatMessage({ defaultMessage: "Expiry date" })}
                        value={answer?.expiry}
                        onChange={(expiry) => onChange({ ...answer, expiry })}
                    />
                )}
            </FilePicker>
        </Stack>
    );
};
